// Server-derived identity for authorization decisions in the UI.
//
// The Harmonate staff flag is taken ONLY from the server (GET conductor/get-current-user, which reads
// public.users for the user in the session token). It is held in memory and is never read from or
// written to browser storage, so a value edited in the browser cannot turn on staff screens.
// The server enforces authorization on every admin and tenant route; this module only decides what the
// UI shows.

let identity = { harmonateUser: false, loaded: false };
const listeners = new Set();

// Upper limit for the identity call made before the first render on a page reload. If the server has not
// answered by then, the app renders with the user as non-staff (the same as a failed call) instead of
// leaving the page blank while it waits. An answer that arrives later is ignored.
export const IDENTITY_TIMEOUT_MS = 10000;

const TIMED_OUT = {};

function notify() {
  listeners.forEach((listener) => {
    try {
      listener({ ...identity });
    } catch (e) {
      // a listener error must not break identity updates
    }
  });
}

export function setServerIdentity({ harmonateUser } = {}) {
  identity = {
    harmonateUser: harmonateUser === true || harmonateUser === 'true',
    loaded: true,
  };
  notify();
}

export function clearServerIdentity() {
  identity = { harmonateUser: false, loaded: false };
  notify();
}

export function isHarmonateUser() {
  return identity.harmonateUser === true;
}

export function isServerIdentityLoaded() {
  return identity.loaded === true;
}

export function subscribeServerIdentity(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Removes the staff flag older builds kept in browser storage, so it cannot linger or be relied on.
export function purgeLegacyStaffFlag() {
  try {
    window.localStorage.removeItem('harmonate_user');
  } catch (e) {
    // storage unavailable: nothing to purge
  }
}

// Loads the identity for the token in storage. Any failure, or no answer within timeoutMs, leaves the
// user as non-staff. Resolves true when the server answered, false otherwise.
export async function loadServerIdentity(fetchImpl, timeoutMs = IDENTITY_TIMEOUT_MS) {
  const doFetch = fetchImpl || (typeof fetch === 'function' ? fetch : null);
  let token = null;
  try {
    token = window.localStorage.getItem('token');
  } catch (e) {
    token = null;
  }
  if (!token || !doFetch) {
    clearServerIdentity();
    return false;
  }
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  let timer = null;
  const request = (async () => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const options = {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    };
    if (controller) options.signal = controller.signal;
    const response = await doFetch(`${base}conductor/get-current-user`, options);
    if (!response || !response.ok) return null;
    const data = await response.json();
    return { harmonateUser: data ? data.harmonate_user : false };
  })();
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => {
      if (controller) {
        try {
          controller.abort();
        } catch (e) {
          // nothing to abort
        }
      }
      resolve(TIMED_OUT);
    }, timeoutMs);
  });
  try {
    const result = await Promise.race([request, timeout]);
    if (result === TIMED_OUT || result === null) {
      setServerIdentity({ harmonateUser: false });
      return false;
    }
    setServerIdentity({ harmonateUser: result.harmonateUser });
    return true;
  } catch (e) {
    setServerIdentity({ harmonateUser: false });
    return false;
  } finally {
    clearTimeout(timer);
    request.catch(() => {});
  }
}
